import { CIVIL_SERVICE_PRICE, formatPrice, getPlan as findPlan, getPlans, NHS_STATEMENT_PRICE, yearlyPerMonth } from '~/constants/pricing'
import type { PremiumPlan, PremiumPlanId } from '~/types/premium'

/**
 * Client-side access to the plan list and price formatting.
 * Variant ids come from runtimeConfig.public so they can differ per environment.
 *
 * Only plans we currently sell are returned; the retired monthly subscription is
 * not among them (see constants/pricing.ts). Plans and prices are in the
 * visitor's currency (usePricing): crowns through Stripe for Czech readers.
 */
export function usePremiumPlans() {
  const config = useRuntimeConfig()
  const { t } = useI18n()
  const { currency, pricing } = usePricing()

  const plans = computed<PremiumPlan[]>(() => getPlans(config.public, currency.value))

  /** A plan on sale by id, including one sold only in its own flow (the tailored PDF, the interview prep). */
  function getPlan(id: PremiumPlanId): PremiumPlan | undefined {
    return findPlan(config.public, id, currency.value)
  }

  function variantId(id: PremiumPlanId): string {
    return getPlan(id)?.variantId ?? ''
  }

  /**
   * The plan every generic "go Premium" button should open.
   * The pack when it is configured, the yearly plan otherwise.
   */
  const defaultPlanId = computed<PremiumPlanId>(() => {
    if (getPlan('job-pack')) return 'job-pack'
    if (getPlan('yearly')) return 'yearly'
    return plans.value[0]?.id ?? 'job-pack'
  })

  /** `$29`, `690 Kč`; a number is in the visitor's currency. */
  function price(plan: PremiumPlan | number): string {
    return typeof plan === 'number' ? pricing.value.format(plan) : formatPrice(plan.price, plan.currency)
  }

  /** `$69.00 / year`, `$29.00` (one-time) */
  function priceWithInterval(plan: PremiumPlan): string {
    const base = formatPrice(plan.price, plan.currency)
    if (!plan.interval) return base
    return `${base} / ${t(plan.interval === 'month' ? 'premium.month' : 'premium.year')}`
  }

  const { prices, format, applicationKitSeparate } = pricing.value
  return {
    plans,
    currency,
    getPlan,
    variantId,
    defaultPlanId,
    price,
    priceWithInterval,
    yearlyPrice: format(prices.yearly),
    yearlyPerMonth: format(yearlyPerMonth(currency.value)),
    exportCreditPrice: format(prices.exportCredit),
    jobPackPrice: format(prices.jobPack),
    tailoredPdfPrice: format(prices.tailoredPdf),
    interviewPrepPrice: format(prices.interviewPrep),
    applicationKitPrice: format(prices.applicationKit),
    /** The kit's parts bought one by one (the tailored PDF and the interview prep). */
    applicationKitSeparatePrice: format(applicationKitSeparate),
    civilServicePrice: formatPrice(CIVIL_SERVICE_PRICE.amount, CIVIL_SERVICE_PRICE.currency),
    nhsStatementPrice: formatPrice(NHS_STATEMENT_PRICE.amount, NHS_STATEMENT_PRICE.currency),
  }
}
